import { GUserInputDesc } from "../../../core/input";
import { ConvertHexColorToVec3, GStack, GetVec2 } from "../../../core/types";
import { GTime, MathLerp, MathVector2Normalize } from "../../../core/utils";
import { TransformFromNDCToWorld } from "../../../helpers/transform";
import { GVectorFieldInjector } from "../../../vectorField";
import { GSceneDesc, SceneBase } from "../../scene";
import { PayneSceneLightSwitch } from "./lightSwitch";
import { ESubSceneState } from "./lightSwitchScripts";

export class InjectionParticle {
    bActive = false;
    bBlack = false;
    bConstSpeed = false;
    CurPosNDC = GetVec2(0, 0);
    CurDir = GetVec2(0, 0);
    SpeedInitial = 0.01;
    SpeedCur = 0.0;

    SpeedMultiplier = 1.0;

    constructor(dirY: number) {
        this.CurDir.y = dirY;
        this.SpeedCur = this.SpeedInitial;
    }

    InitRand() {
        this.bActive = true;
        //generate random initial pos
        this.CurPosNDC.x = MathLerp(-0.9, -0.6, Math.random());
        this.CurPosNDC.y = MathLerp(-1.0, 1.0, Math.random());

        this.SpeedCur = this.SpeedInitial;

        //generate rand dir
        {
            this.CurDir.x = 0.9;
            this.CurDir.y = MathLerp(-0.5, 0.5, Math.sqrt(Math.random()));

            this.CurDir.x -= this.CurPosNDC.x;
            this.CurDir.y -= this.CurPosNDC.y;
        }

        /* this.CurDir.x = 1.0;
        this.CurDir.y = MathLerp(-0.25, 0.25, Math.random());
        this.CurDir.y -= this.CurPosNDC.y * 0.25;
        this.CurDir.y = Math.min(0.8, this.CurDir.y + 0.25);

        if (this.CurPosNDC.y > 0.5) {
            this.CurDir.y = Math.min(0.0, this.CurDir.y);
        } */

        this.CurDir = MathVector2Normalize(this.CurDir);

        this.SpeedMultiplier = 0.1 + Math.random() * 0.9;
    }
    OnUpdate() {
        if (this.bActive) {
            this.CurDir.y += Math.sin(GTime.Cur) * 0.01 + Math.sin(GTime.Cur * 15.14) * 0.05;

            this.CurPosNDC.x =
                this.CurPosNDC.x + this.CurDir.x * this.SpeedCur * GTime.Delta * 60 * this.SpeedMultiplier;
            this.CurPosNDC.y =
                this.CurPosNDC.y + this.CurDir.y * this.SpeedCur * GTime.Delta * 60 * this.SpeedMultiplier;

            if (!this.bConstSpeed) {
                this.SpeedCur += 0.002;
            } else {
                this.SpeedCur += 0.00001;
            }

            if (this.CurPosNDC.x > 1.0 || Math.abs(this.CurPosNDC.y) > 1.0) {
                this.bActive = false;
            }
        }
    }
}

export function InjectImpulse(gl: WebGL2RenderingContext, scene: PayneSceneLightSwitch) {
    gl.disable(gl.BLEND);
    const vecField = scene.VectorFieldProjector;
    const curSourceIndex = vecField.CurDensTextureIndex;
    gl.viewport(0, 0, vecField.DensityNumCells.x, vecField.DensityNumCells.y);
    gl.bindFramebuffer(gl.FRAMEBUFFER, vecField.DensityFramebuffer[curSourceIndex]);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

    //map input to world
    const posWS = GStack.TempVec3;
    TransformFromNDCToWorld(GUserInputDesc.InputPosCurNDC, -GSceneDesc.Camera.Position.z, posWS);
    //map world to density plane
    const sceneExtent = SceneBase.UniformBuffer.Params.SceneBoundsExtent;
    posWS.x /= sceneExtent.x;
    posWS.y /= sceneExtent.y;

    GVectorFieldInjector.GInstance?.InjectImpulse(gl, vecField, GetVec2(posWS.x, posWS.y));
}

export function InjectProjectorTextureToDensity(gl: WebGL2RenderingContext, scene: PayneSceneLightSwitch) {
    if (scene.ProjectorTexture.bLoaded) {
        const colorFilter = GStack.TempVec3;
        colorFilter.Set(SceneBase.UniformBuffer.Params.ProjectorColor);
        colorFilter.Mul(scene.RenderToDensityColorScale);

        GVectorFieldInjector.GInstance!.InjectDensityTextured(
            gl,
            scene.VectorFieldProjector,
            GetVec2(0, 0),
            GetVec2(1.375, 1),
            scene.ProjectorTexture.InnerTexture,
            colorFilter,
        );
    }
}

export function DensityRenderCenterImpulse(gl: WebGL2RenderingContext, scene: PayneSceneLightSwitch) {
    const fireInMichelle = scene.CurSceneState == ESubSceneState.Michelle && Math.random() < 0.2;
    if (fireInMichelle || scene.CurSceneState == ESubSceneState.Nicole) {
        scene.DensityRenderParticleUp = new InjectionParticle(1.0);
        scene.DensityRenderParticleUp.bActive = true;
        scene.DensityRenderParticleUp.bConstSpeed = true;
        scene.DensityRenderParticleUp.SpeedCur = 0.001 + 0.05 * Math.random() * 0.75;

        scene.DensityRenderParticleDown = new InjectionParticle(-1.0);
        scene.DensityRenderParticleDown.bActive = true;
        scene.DensityRenderParticleDown.bConstSpeed = true;
        scene.DensityRenderParticleDown.SpeedCur = 0.001 + 0.05 * Math.random() * 0.75;
        GVectorFieldInjector.GInstance!.InjectImpulse(gl, scene.VectorFieldProjector, GetVec2(0, 0));
    } else {
    }
}

export function DensityRenderBullet(gl: WebGL2RenderingContext, scene: PayneSceneLightSwitch) {
    if (scene.CurSceneState == ESubSceneState.Nicole) {
        scene.DensityRenderParticle.bBlack = false;
    } else {
        scene.DensityRenderParticle.bBlack = true;
    }
    if (scene.CurSceneState !== ESubSceneState.Rose) {
        scene.DensityRenderParticle.InitRand();
        GVectorFieldInjector.GInstance!.InjectImpulse(
            gl,
            scene.VectorFieldProjector,
            scene.DensityRenderParticle.CurPosNDC,
        );
    }
}

export function UpdateAndRenderPaintParticle(
    gl: WebGL2RenderingContext,
    inP: InjectionParticle,
    scene: PayneSceneLightSwitch,
) {
    inP.OnUpdate();

    if (inP.bActive) {
        let color = scene.DensityRenderColor;
        if (inP.bBlack) {
            color = GStack.TempVec3_4;
            color.Set3(0, 0, 0.1);
        }
        GVectorFieldInjector.GInstance!.InjectDensity(gl, scene.VectorFieldProjector, inP.CurPosNDC, color);

        const velScale = 2.0;
        GVectorFieldInjector.GInstance!.InjectVelocity(
            gl,
            scene.VectorFieldProjector,
            inP.CurPosNDC,
            GetVec2(inP.CurDir.x * inP.SpeedCur * velScale, inP.CurDir.y * inP.SpeedCur * velScale),
            GVectorFieldInjector.GInstance!.AppliedVelocityRadius,
            GVectorFieldInjector.GInstance!.AppliedVelocityStrength,
        );
    }
}

export function DensityRenderEventUpdate(gl: WebGL2RenderingContext, scene: PayneSceneLightSwitch) {
    //Apply rnd vorticity
    scene.VectorFieldProjector.VorticityScale = MathLerp(0, 5, (Math.sin(GTime.Cur) + 1.0) * 0.5);
    ConvertHexColorToVec3(scene.DensityRenderColorHex, scene.DensityRenderColor);

    UpdateAndRenderPaintParticle(gl, scene.DensityRenderParticle, scene);
    UpdateAndRenderPaintParticle(gl, scene.DensityRenderParticleUp, scene);
    UpdateAndRenderPaintParticle(gl, scene.DensityRenderParticleDown, scene);
}
